'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PublicLink } from '@/components/PublicLink';
import {
  AlertCircle,
  AlertTriangle,
  Download,
  TrendingUp,
} from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import { RecurringConsentBlock } from '@/components/billing/recurring-consent-block';
import { AnimatedContainer } from '@/components/ui/animated-container';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBillingActions } from '@/hooks/use-billing-actions';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  PLAN_NAME_LOOKUP,
  buildDisplayedPlanChoices,
  isBillingPaidPlanSlug,
  normalizeBillingPlanSlug,
  type SelectablePlanSlug,
} from './billing-plan-choices';
import type {
  BillingUsageSummary,
  InvoiceSummary,
  PlanChangePreview,
  PublicPlanPrice,
  SubscriptionInfo,
  UpcomingInvoice,
  UsageSeriesPoint,
} from '@/types/billing';
import {
  DEFAULT_BILLING_CURRENCY,
  resolveBillingCurrency,
} from '@/lib/billing-pricing';
import type { IndexedPublicPlanPrices } from '@/lib/public-plan-prices-snapshot';
import { formatPublicDecimalAmount } from '@/lib/public-plan-prices';
import { maybeUtcDate, utcNow } from '@/lib/time';
import {
  determinePlanChangeBehavior,
  type PaidPlanSlug,
  type PlanChangeDecision,
  shouldPreviewPaidPlanChange,
} from '@/lib/plan-change';
import { AppPageShell, PageHero, PageIntro, PageSection } from '@/components/site/page-shell';
import { parseCheckoutPlan, withCheckoutPlanLink } from '@/lib/auth-intent';
import {
  buildScheduleCancelConfirmCopy,
  ScheduleCancelConfirmDialogBody,
} from './schedule-cancel-confirm-dialog';

const MONTH_OPTIONS = [
  { value: 'all', label: 'Все месяцы' },
  { value: '01', label: 'Январь' },
  { value: '02', label: 'Февраль' },
  { value: '03', label: 'Март' },
  { value: '04', label: 'Апрель' },
  { value: '05', label: 'Май' },
  { value: '06', label: 'Июнь' },
  { value: '07', label: 'Июль' },
  { value: '08', label: 'Август' },
  { value: '09', label: 'Сентябрь' },
  { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' },
  { value: '12', label: 'Декабрь' },
] as const;

type NoticeTone = 'info' | 'warning' | 'danger';

interface Notice {
  id: string;
  tone: NoticeTone;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const STATUS_LABEL_LOOKUP: Record<string, string> = {
  active: 'Активен',
  pending_verification: 'Требуется проверка карты',
  blocked_duplicate: 'Требуется действие',
  canceled: 'Отменен',
  past_due: 'Платеж просрочен',
  unpaid: 'Не оплачен',
  trialing: 'Пробный период',
  open: 'Открыт',
  draft: 'Черновик',
  paid: 'Оплачен',
  void: 'Аннулирован',
  uncollectible: 'Безнадежный',
};

const AUTO_REFRESH_INTERVAL_MS = 30000;
const AUTO_REFRESH_MAX_ATTEMPTS = 6;

function toTitle(value?: string | null): string {
  if (!value) {
    return '';
  }
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function formatPlanName(value?: string | null): string {
  if (!value) {
    return '';
  }
  const normalized = value.toLowerCase();
  if (normalized === 'hobby' || normalized === 'basic' || normalized === 'pro') {
    return PLAN_NAME_LOOKUP[normalized as SelectablePlanSlug];
  }
  if (normalized === 'custom') {
    return 'Индивидуальный';
  }
  return toTitle(value);
}

function formatStatusLabel(value?: string | null): string {
  if (!value) {
    return '';
  }
  return STATUS_LABEL_LOOKUP[value.toLowerCase()] ?? toTitle(value);
}

function formatCurrency(cents?: number | null, currency?: string | null): string {
  if (cents === null || cents === undefined) {
    return '—';
  }
  const normalizedCurrency = (currency ?? DEFAULT_BILLING_CURRENCY).toUpperCase();
  const formatter = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: normalizedCurrency,
  });
  return formatter.format(cents / 100);
}

function formatDateLabel(value?: string | null, fallback = '—'): string {
  const date = maybeUtcDate(value);
  if (!date) {
    return fallback;
  }
  return format(date, 'd MMM yyyy', { locale: ru });
}

function formatDateShort(value?: string | null): string {
  const date = maybeUtcDate(value);
  if (!date) {
    return '—';
  }
  return format(date, 'd MMM', { locale: ru });
}

function formatIntervalFrequency(interval?: string | null, count?: number | null): string {
  if (!interval) {
    return 'период';
  }
  const normalized = interval.toLowerCase();
  if (!count || count === 1) {
    if (normalized === 'month') return 'месяц';
    if (normalized === 'year') return 'год';
    if (normalized === 'week') return 'неделю';
    if (normalized === 'day') return 'день';
    return normalized;
  }
  if (normalized === 'month') return `${count} мес.`;
  if (normalized === 'year') return `${count} г.`;
  if (normalized === 'week') return `${count} нед.`;
  if (normalized === 'day') return `${count} дн.`;
  return `${count} ${normalized}`;
}

function formatRecurringCadence(interval?: string | null, count?: number | null): string {
  const normalized = interval?.toLowerCase();
  const frequency = formatIntervalFrequency(interval, count);
  if (!count || count === 1) {
    if (normalized === 'month') return 'каждый месяц';
    if (normalized === 'year') return 'каждый год';
    if (normalized === 'week') return 'каждую неделю';
    if (normalized === 'day') return 'каждый день';
    return `каждый ${frequency}`;
  }
  return `каждые ${frequency}`;
}

function buildImmediateCheckoutPlanPreview(price: PublicPlanPrice): PlanChangePreview {
  return {
    plan_slug: price.plan_slug,
    plan_name: price.plan_name,
    currency: price.currency,
    amount_due_now: price.amount_cents,
    next_interval_amount: price.amount_cents,
    next_interval: price.interval,
    next_interval_count: price.interval_count,
    proration_lines: [],
  };
}

function statusMeta(status?: string | null): { label: string; tone: NoticeTone } {
  const normalized = (status ?? '').toLowerCase();
  switch (normalized) {
    case 'past_due':
    case 'unpaid':
      return { label: 'Платеж просрочен', tone: 'danger' };
    case 'trialing':
      return { label: 'Пробный период', tone: 'info' };
    case 'pending_verification':
      return { label: 'Подтвердите карту', tone: 'warning' };
    case 'blocked_duplicate':
      return { label: 'Требуется действие', tone: 'danger' };
    case 'canceled':
      return { label: 'Отменен', tone: 'warning' };
    default:
      return { label: toTitle(normalized || 'Активен'), tone: 'info' };
  }
}

function invoiceStatusTone(status?: string | null): NoticeTone {
  const normalized = (status ?? '').toLowerCase();
  if (normalized === 'open' || normalized === 'draft') {
    return 'warning';
  }
  if (normalized === 'paid' || normalized === 'void') {
    return 'info';
  }
  if (normalized === 'uncollectible') {
    return 'danger';
  }
  return 'info';
}

async function readJsonOrNull<T>(response: Response): Promise<T | null> {
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') ?? '';

  if (response.ok) {
    if (!contentType.includes('application/json')) {
      return null;
    }
    return (await response.json()) as T;
  }

  if (response.status === 404) {
    return null;
  }

  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as { error?: string; detail?: string };
      throw new Error(payload.error || payload.detail || response.statusText || 'Запрос завершился ошибкой');
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
    }
  }

  const fallbackText = await response.text();
  throw new Error(fallbackText || response.statusText || 'Запрос завершился ошибкой');
}

async function extractError(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as { error?: string; detail?: string };
    return data.error || data.detail || response.statusText || 'Запрос завершился ошибкой';
  } catch {
    return response.statusText || 'Запрос завершился ошибкой';
  }
}

function aggregateDailyTotals(points?: UsageSeriesPoint[]): number[] {
  if (!points?.length) {
    return [];
  }

  const totals = new Map<string, number>();

  points.forEach((point) => {
    if (!point.hour_start) {
      return;
    }
    const key = point.hour_start.slice(0, 10);
    totals.set(key, (totals.get(key) ?? 0) + point.billable_requests);
  });

  return Array.from(totals.entries())
    .sort(([a], [b]) => (a > b ? 1 : -1))
    .map(([, total]) => total);
}

function StatusChip({
  children,
  tone = 'info',
}: {
  children: React.ReactNode;
  tone?: NoticeTone;
}) {
  const toneClass =
    tone === 'danger'
      ? 'bg-red-100 text-red-700 border-red-200'
      : tone === 'warning'
        ? 'bg-amber-100 text-amber-800 border-amber-200'
        : 'bg-slate-100 text-[var(--site-ink)] border-[color:var(--site-border-soft)]';

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border ${toneClass}`}>
      {children}
    </span>
  );
}

function BillingMetricCardSkeleton() {
  return (
    <div className="rounded-lg border border-[color:var(--site-border-soft)] p-4">
      <div className="space-y-3">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
  );
}

function BillingOverviewSkeleton() {
  return (
    <Card data-testid="billing-overview-skeleton">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center justify-between gap-3">
          <Skeleton className="h-7 w-44" />
          <Skeleton className="h-4 w-40" />
        </CardTitle>
        <CardDescription>
          <Skeleton className="h-4 w-56" />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <Skeleton className="h-7 w-28 rounded-full" />
          <Skeleton className="h-7 w-32 rounded-full" />
          <Skeleton className="h-7 w-36 rounded-full" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-36" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="billing-quick-actions-skeleton">
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </CardContent>
    </Card>
  );
}

function BillingUsageSkeleton({ showOverageCard }: { showOverageCard: boolean }) {
  return (
    <Card data-testid="billing-usage-skeleton">
      <CardHeader className="pb-4">
        <CardTitle>
          <Skeleton className="h-7 w-56" />
        </CardTitle>
        <CardDescription>
          <Skeleton className="h-4 w-64" />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-4 w-52" />
            <Skeleton className="h-4 w-12" />
          </div>
          <Skeleton className="h-3 w-full rounded-full" />
          <Skeleton className="h-3 w-5/6" />
        </div>
        <div className="rounded-lg bg-[var(--site-surface-inset)] p-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        </div>
        <div className={`grid gap-4 ${showOverageCard ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          <BillingMetricCardSkeleton />
          <BillingMetricCardSkeleton />
          <BillingMetricCardSkeleton />
          {showOverageCard ? <BillingMetricCardSkeleton /> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function BillingUpcomingChargeSkeleton() {
  return (
    <Card data-testid="billing-upcoming-charge-skeleton">
      <CardHeader className="pb-4">
        <CardTitle>
          <Skeleton className="h-7 w-40" />
        </CardTitle>
        <CardDescription>
          <Skeleton className="h-4 w-44" />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-10 w-36" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="space-y-2 rounded-lg border border-[color:var(--site-border-soft)] p-4">
          <div className="flex items-center justify-between gap-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="flex items-center justify-between gap-4">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-14" />
          </div>
          <div className="flex items-center justify-between gap-4">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <Skeleton className="h-3 w-full" />
      </CardContent>
    </Card>
  );
}

function BillingInvoicesSkeleton() {
  return (
    <Card data-testid="billing-invoices-skeleton">
      <CardHeader className="pb-4">
        <CardTitle>
          <Skeleton className="h-7 w-24" />
        </CardTitle>
        <CardDescription>
          <Skeleton className="h-4 w-64" />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Skeleton className="h-10 w-[160px] rounded-md" />
          <Skeleton className="h-10 w-[180px] rounded-md" />
        </div>
        <div className="rounded-lg border border-[color:var(--site-border-soft)]">
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-4 border-b border-[color:var(--site-border-soft)] px-4 py-3">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-14" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-14" />
            <Skeleton className="h-4 w-12" />
          </div>
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`billing-invoices-skeleton-row-${index}`}
              className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-4 border-b border-[color:var(--site-border-soft)] px-4 py-4 last:border-b-0"
            >
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-4 w-16 justify-self-end" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface BillingHubPageClientProps {
  initialPublicPlanPrices: IndexedPublicPlanPrices;
}

function BillingCheckoutIntentHandler({
  enabled,
  onCheckoutIntent,
}: {
  enabled: boolean;
  onCheckoutIntent: (plan: 'basic' | 'pro') => void;
}) {
  const handledCheckoutIntentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const checkoutIntentPlan = parseCheckoutPlan(searchParams.get('plan'));
    const shouldAutoOpenCheckout =
      searchParams.get('intent') === 'checkout' && checkoutIntentPlan !== null;

    if (!shouldAutoOpenCheckout || !checkoutIntentPlan) {
      return;
    }

    const intentKey = `${checkoutIntentPlan}:${searchParams.toString()}`;
    if (handledCheckoutIntentRef.current === intentKey) {
      return;
    }
    handledCheckoutIntentRef.current = intentKey;

    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.delete('intent');
    nextParams.delete('plan');
    const nextQuery = nextParams.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);

    onCheckoutIntent(checkoutIntentPlan);
  }, [enabled, onCheckoutIntent]);

  return null;
}

export default function BillingHubPageClient({
  initialPublicPlanPrices,
}: BillingHubPageClientProps) {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const {
    startCheckout,
    changePlan,
    previewPlanChange,
    cancelPendingPlanChange,
  } = useBillingActions();
  const { toast } = useToast();
  const invoicesRef = useRef<HTMLDivElement | null>(null);

  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const publicPlanPrices = initialPublicPlanPrices;
  const [billingUsage, setBillingUsage] = useState<BillingUsageSummary | null | undefined>(undefined);
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState<UpcomingInvoice | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasLoadedBilling, setHasLoadedBilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingCheckoutPlan, setPendingCheckoutPlan] = useState<SelectablePlanSlug | null>(null);
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [previewLoadingPlan, setPreviewLoadingPlan] = useState<PaidPlanSlug | null>(null);
  const [planPreview, setPlanPreview] = useState<PlanChangePreview | null>(null);
  const [planPreviewSlug, setPlanPreviewSlug] = useState<PaidPlanSlug | null>(null);
  const [planPreviewBehavior, setPlanPreviewBehavior] = useState<PlanChangeDecision | null>(null);
  const [isPlanConfirmOpen, setIsPlanConfirmOpen] = useState(false);
  const [hasPlanChangeRecurringConsent, setHasPlanChangeRecurringConsent] = useState(false);
  const [isConfirmingPlanChange, setIsConfirmingPlanChange] = useState(false);
  const [isScheduleCancelConfirmOpen, setIsScheduleCancelConfirmOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isCancellingPendingPlan, setIsCancellingPendingPlan] = useState(false);
  const [autoRefreshRemaining, setAutoRefreshRemaining] = useState(0);
  const [invoiceYearFilter, setInvoiceYearFilter] = useState('all');
  const [invoiceMonthFilter, setInvoiceMonthFilter] = useState<(typeof MONTH_OPTIONS)[number]['value']>('all');
  const planPreviewLines = planPreview?.proration_lines ?? [];
  const hasProrationAdjustments = planPreviewLines.some((line) => Boolean(line?.proration));
  const planPreviewDeferredDate =
    planPreview?.pending_plan_effective_at ?? planPreviewBehavior?.deferredEffectiveAt ?? null;
  const pendingPlanSlug = subscription?.pending_plan_slug ?? null;
  const pendingPlanEffectiveAt = subscription?.pending_plan_effective_at ?? null;
  const pendingPlanName =
    (pendingPlanSlug && PLAN_NAME_LOOKUP[pendingPlanSlug as SelectablePlanSlug]) ||
    (pendingPlanSlug ? formatPlanName(pendingPlanSlug) : null);

  const resetBillingState = useCallback(() => {
    setSubscription(null);
    setBillingUsage(undefined);
    setInvoices([]);
    setUpcomingInvoice(null);
    setIsLoading(false);
    setIsRefreshing(false);
    setHasLoadedBilling(false);
    setError(null);
    setPendingCheckoutPlan(null);
    setIsPlanDialogOpen(false);
    setPreviewLoadingPlan(null);
    setPlanPreview(null);
    setPlanPreviewSlug(null);
    setPlanPreviewBehavior(null);
    setIsPlanConfirmOpen(false);
    setHasPlanChangeRecurringConsent(false);
    setIsConfirmingPlanChange(false);
    setIsScheduleCancelConfirmOpen(false);
    setIsCancelling(false);
    setIsResuming(false);
    setIsCancellingPendingPlan(false);
    setAutoRefreshRemaining(0);
  }, []);

  const fetchBillingData = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const [subscriptionRes, usageRes, invoicesRes, upcomingRes] = await Promise.all([
          fetch('/api/billing/subscription', { cache: 'no-store' }),
          fetch('/api/billing/usage', { cache: 'no-store' }),
          fetch('/api/billing/invoices?limit=250', { cache: 'no-store' }),
          fetch('/api/billing/upcoming-invoice', { cache: 'no-store' }),
        ]);

        const subscriptionData = await readJsonOrNull<SubscriptionInfo | null>(subscriptionRes);
        const usageData = await readJsonOrNull<BillingUsageSummary | null>(usageRes);
        const invoicesData = await readJsonOrNull<
          InvoiceSummary[] | { items?: InvoiceSummary[] } | null
        >(invoicesRes);
        const upcomingData = await readJsonOrNull<UpcomingInvoice | null>(upcomingRes);

        setSubscription(subscriptionData ?? null);
        setBillingUsage(usageData ?? null);
        const normalizedInvoices = Array.isArray(invoicesData)
          ? invoicesData
          : Array.isArray(invoicesData?.items)
            ? invoicesData?.items ?? []
            : [];
        setInvoices(normalizedInvoices);
        setUpcomingInvoice(upcomingData ?? null);
      } catch (fetchError) {
        console.error('Failed to load billing details', fetchError);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Сейчас не удалось загрузить данные по оплате.',
        );
      } finally {
        setHasLoadedBilling(true);
        if (silent) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [],
  );

  const refreshBillingPage = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!isAuthenticated) {
        resetBillingState();
        return;
      }
      await fetchBillingData({ silent: options?.silent });
    },
    [fetchBillingData, isAuthenticated, resetBillingState],
  );

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated) {
      resetBillingState();
      return;
    }

    void refreshBillingPage();
  }, [isAuthLoading, isAuthenticated, refreshBillingPage, resetBillingState]);

  useEffect(() => {
    if (!isAuthenticated || !hasLoadedBilling) {
      return;
    }
    setAutoRefreshRemaining(AUTO_REFRESH_MAX_ATTEMPTS);
  }, [hasLoadedBilling, isAuthenticated, subscription?.plan_slug, pendingPlanSlug]);

  useEffect(() => {
    if (!isAuthenticated || !hasLoadedBilling || autoRefreshRemaining <= 0) {
      return;
    }
    const timer = setTimeout(() => {
      setAutoRefreshRemaining((prev) => prev - 1);
      void refreshBillingPage({ silent: true });
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [autoRefreshRemaining, hasLoadedBilling, isAuthenticated, refreshBillingPage]);

  const effectivePlanSlug = useMemo(() => {
    if (subscription?.plan_slug) {
      return subscription.plan_slug.toLowerCase();
    }
    if (hasLoadedBilling) {
      return user?.plan?.toLowerCase() ?? null;
    }
    return null;
  }, [hasLoadedBilling, subscription?.plan_slug, user?.plan]);
  const planLabel = useMemo(() => {
    if (!hasLoadedBilling) {
      return 'Загружаем данные подписки…';
    }
    if (effectivePlanSlug) {
      return formatPlanName(effectivePlanSlug);
    }
    if (subscription) {
      return 'Тариф синхронизируется…';
    }
    if (user?.plan) {
      return formatPlanName(user.plan);
    }
    return 'Тариф недоступен';
  }, [effectivePlanSlug, hasLoadedBilling, subscription, user?.plan]);
  const overageRate = typeof subscription?.overage_price === 'number' ? subscription.overage_price : null;
  const normalizedPlanSlug = normalizeBillingPlanSlug(effectivePlanSlug);
  const isHobbyPlan = normalizedPlanSlug === 'hobby';
  const planSupportsOverage =
    overageRate !== null
      ? overageRate > 0
      : normalizedPlanSlug
        ? isBillingPaidPlanSlug(normalizedPlanSlug)
        : false;
  const normalizedStatus = (subscription?.status ?? '').toLowerCase();
  const statusDetails = statusMeta(subscription?.status);
  const isPendingVerification = normalizedStatus === 'pending_verification';
  const isAccountBlocked = normalizedStatus === 'blocked_duplicate';
  const isPastDue = normalizedStatus === 'past_due' || normalizedStatus === 'unpaid';
  const isCanceled = normalizedStatus === 'canceled';
  const isCancelScheduled = Boolean(subscription?.cancel_at_period_end) && !isCanceled;

  const includedUnits = subscription?.included_units ?? 0;
  const usageLoaded = billingUsage !== undefined;
  const usedUnits = billingUsage?.total_usage ?? 0;
  const usagePercentOfPlan =
    includedUnits > 0 ? Math.min(100, Math.round((usedUnits / includedUnits) * 100)) : 0;
  const usageTotalsReady = usageLoaded;
  const usageProgressWidth = usageTotalsReady && includedUnits > 0 ? usagePercentOfPlan : 0;
  const usageBarColor = !usageTotalsReady
    ? 'bg-[color:rgba(23,38,59,0.12)]'
    : usagePercentOfPlan >= 100
      ? planSupportsOverage
        ? 'bg-[var(--site-accent-text)]'
        : 'bg-red-500'
      : usagePercentOfPlan >= 80
        ? 'bg-amber-500'
        : 'bg-[var(--site-accent-text)]';
  const usageThresholds = isHobbyPlan
    ? [
        { value: 0.5, label: '50% предупреждение' },
        { value: 0.8, label: '80% напоминание об апгрейде' },
        { value: 1, label: '100% жесткий лимит' },
      ]
    : [
        { value: 0.5, label: '50% предупреждение' },
        { value: 0.8, label: '80% напоминание' },
        { value: 1, label: 'Начинается перерасход' },
      ];
  const remainingUnits = Math.max(includedUnits - usedUnits, 0);
  const usageResetDate = billingUsage?.as_of ?? subscription?.current_period_end ?? null;
  const usageResetFull = usageResetDate ? formatDateLabel(usageResetDate) : 'ваше следующее продление';
  const usageResetShort = usageResetDate ? formatDateShort(usageResetDate) : 'следующее продление';
  const displayedIncludedUnits = includedUnits.toLocaleString();
  const displayedUsedUnits = usageTotalsReady ? usedUnits.toLocaleString() : '—';
  const displayedUsagePercent = usageTotalsReady && includedUnits > 0 ? `${usagePercentOfPlan}%` : '—';
  const displayedRemainingUnits = usageTotalsReady ? remainingUnits.toLocaleString() : '—';
  const overageUnits = Math.max(usedUnits - includedUnits, 0);
  const invoicesCurrency = useMemo(() => {
    const invoiceWithCurrency = invoices.find(
      (invoice) => typeof invoice.currency === 'string' && invoice.currency.length,
    );
    return invoiceWithCurrency?.currency ?? null;
  }, [invoices]);
  const subscriptionCurrency = resolveBillingCurrency({
    upcomingInvoice,
    invoices,
    publicPlanPrices,
    currentPaidPlan:
      normalizedPlanSlug && isBillingPaidPlanSlug(normalizedPlanSlug) ? normalizedPlanSlug : null,
  });
  const projectedOverageCents =
    planSupportsOverage && overageRate !== null ? Math.round(overageUnits * overageRate * 100) : 0;
  const hasOverageCharges = planSupportsOverage && projectedOverageCents > 0;
  const projectedOverageDisplay =
    planSupportsOverage && overageRate !== null
      ? formatCurrency(projectedOverageCents, subscriptionCurrency)
      : '—';
  const overageRateDisplay =
    planSupportsOverage && overageRate !== null
      ? `${formatPublicDecimalAmount(overageRate, subscriptionCurrency)} за запрос`
      : null;
  const displayedPlanChoices = useMemo(
    () => buildDisplayedPlanChoices(publicPlanPrices),
    [publicPlanPrices],
  );

  const invoiceYears = useMemo(() => {
    const years = new Set<string>();
    invoices.forEach((invoice) => {
      const date = maybeUtcDate(invoice.period_end ?? invoice.period_start ?? invoice.created);
      if (date) {
        years.add(String(date.getUTCFullYear()));
      }
    });
    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const date = maybeUtcDate(invoice.period_end ?? invoice.period_start ?? invoice.created);
      if (!date) {
        return invoiceYearFilter === 'all' && invoiceMonthFilter === 'all';
      }
      const year = String(date.getUTCFullYear());
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      if (invoiceYearFilter !== 'all' && year !== invoiceYearFilter) {
        return false;
      }
      if (invoiceMonthFilter !== 'all' && month !== invoiceMonthFilter) {
        return false;
      }
      return true;
    });
  }, [invoices, invoiceYearFilter, invoiceMonthFilter]);

  const cardExpiryMonth =
    subscription?.card_exp_month ?? subscription?.default_payment_method?.exp_month ?? null;
  const cardExpiryYear =
    subscription?.card_exp_year ?? subscription?.default_payment_method?.exp_year ?? null;
  const cardExpiresSoon = useMemo(() => {
    if (!cardExpiryMonth || !cardExpiryYear) {
      return false;
    }
    const now = utcNow();
    const monthsUntilExpiry =
      (cardExpiryYear - now.getUTCFullYear()) * 12 +
      (cardExpiryMonth - (now.getUTCMonth() + 1));
    return monthsUntilExpiry <= 2;
  }, [cardExpiryMonth, cardExpiryYear]);

  const currentPaidPlan = useMemo(() => {
    const normalized = normalizeBillingPlanSlug(subscription?.plan_slug);
    if (!normalized || !isBillingPaidPlanSlug(normalized)) {
      return null;
    }
    return normalized;
  }, [subscription?.plan_slug]);
  const scheduleCancelPlanName = currentPaidPlan ? formatPlanName(currentPaidPlan) : '';
  const scheduleCancelPeriodEndLabel = formatDateLabel(
    subscription?.current_period_end,
    'конца текущего периода',
  );
  const scheduleCancelConfirmCopy = useMemo(
    () =>
      buildScheduleCancelConfirmCopy({
        planName: scheduleCancelPlanName,
        periodEndLabel: scheduleCancelPeriodEndLabel,
      }),
    [scheduleCancelPeriodEndLabel, scheduleCancelPlanName],
  );

  const canInlineChangePlan = useCallback(
    (target: SelectablePlanSlug | null) => {
      if (!target || !isBillingPaidPlanSlug(target)) {
        return false;
      }
      if (!currentPaidPlan) {
        return false;
      }
      return currentPaidPlan !== target;
    },
    [currentPaidPlan],
  );

  const handlePlanCheckout = useCallback(
    async (plan: SelectablePlanSlug, mode?: 'subscription' | 'setup') => {
      if (pendingCheckoutPlan || previewLoadingPlan) {
        return;
      }

      const beginCheckoutRedirect = async (
        checkoutPlan: SelectablePlanSlug,
        checkoutMode?: 'subscription' | 'setup',
      ) => {
        setPendingCheckoutPlan(checkoutPlan);
        try {
          await startCheckout({ plan: checkoutPlan, mode: checkoutMode });
          setIsPlanDialogOpen(false);
        } catch {
          setPendingCheckoutPlan(null);
        }
      };

      if (isBillingPaidPlanSlug(plan)) {
        const paidPlan = plan;
        if (currentPaidPlan === paidPlan) {
          const recoveryPrice = publicPlanPrices[paidPlan];
          setPlanPreview(buildImmediateCheckoutPlanPreview(recoveryPrice));
          setPlanPreviewSlug(paidPlan);
          setPlanPreviewBehavior({
            direction: 'none',
            effectiveImmediately: true,
            deferredEffectiveAt: null,
          });
          setHasPlanChangeRecurringConsent(false);
          setIsPlanConfirmOpen(true);
          setIsPlanDialogOpen(false);
          return;
        }

        if (!shouldPreviewPaidPlanChange(currentPaidPlan, paidPlan)) {
          const immediateCheckoutPrice = publicPlanPrices[paidPlan];
          setPlanPreview(buildImmediateCheckoutPlanPreview(immediateCheckoutPrice));
          setPlanPreviewSlug(paidPlan);
          setPlanPreviewBehavior({
            direction: 'upgrade',
            effectiveImmediately: true,
            deferredEffectiveAt: null,
          });
          setHasPlanChangeRecurringConsent(false);
          setIsPlanConfirmOpen(true);
          setIsPlanDialogOpen(false);
          return;
        }

        const behavior = determinePlanChangeBehavior({
          currentPlan: currentPaidPlan,
          targetPlan: paidPlan,
          currentPeriodEnd: subscription?.current_period_end ?? null,
        });
        setPreviewLoadingPlan(paidPlan);
        try {
          const preview = await previewPlanChange(paidPlan, {
            effectiveImmediately: behavior.effectiveImmediately,
          });
          setPlanPreview(preview);
          setPlanPreviewSlug(paidPlan);
          setPlanPreviewBehavior(behavior);
          setHasPlanChangeRecurringConsent(false);
          setIsPlanConfirmOpen(true);
          setIsPlanDialogOpen(false);
        } catch {
          // previewPlanChange displays toast
        } finally {
          setPreviewLoadingPlan(null);
        }
        return;
      }

      await beginCheckoutRedirect(plan, mode);
    },
    [
      pendingCheckoutPlan,
      previewLoadingPlan,
      previewPlanChange,
      startCheckout,
      setIsPlanDialogOpen,
      currentPaidPlan,
      publicPlanPrices,
      subscription?.current_period_end,
    ],
  );

  const handleRecoverCurrentPlan = useCallback(async () => {
    if (!currentPaidPlan) {
      return;
    }
    await handlePlanCheckout(currentPaidPlan, 'subscription');
  }, [currentPaidPlan, handlePlanCheckout]);

  const handleScheduleCancel = useCallback(async (): Promise<boolean> => {
    if (isCancelling) {
      return false;
    }
    setIsCancelling(true);
    try {
      const response = await fetch('/api/billing/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ at_period_end: true }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      toast({
        title: 'Отмена запланирована',
        description: 'Вы можете продолжать пользоваться сервисом до конца текущего периода.',
      });
      await refreshBillingPage({ silent: true });
      setIsScheduleCancelConfirmOpen(false);
      return true;
    } catch (cancelError) {
      setIsScheduleCancelConfirmOpen(false);
      toast({
        variant: 'destructive',
        title: 'Не удалось запланировать отмену',
        description:
          cancelError instanceof Error ? cancelError.message : 'Повторите попытку через минуту.',
      });
      return false;
    } finally {
      setIsCancelling(false);
    }
  }, [isCancelling, refreshBillingPage, toast]);

  const handleDowngradeToHobby = useCallback(async () => {
    const success = await handleScheduleCancel();
    if (success) {
      setIsPlanDialogOpen(false);
    }
  }, [handleScheduleCancel, setIsPlanDialogOpen]);

  const handleScheduleCancelDialogOpenChange = useCallback(
    (open: boolean) => {
      if (isCancelling) {
        return;
      }
      setIsScheduleCancelConfirmOpen(open);
    },
    [isCancelling],
  );

  const handleResume = useCallback(async () => {
    if (isResuming) {
      return;
    }
    setIsResuming(true);
    try {
      const response = await fetch('/api/billing/resume', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ at_period_end: false }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      toast({
        title: 'Подписка возобновлена',
        description: 'Мы убрали запланированную отмену.',
      });
      await refreshBillingPage({ silent: true });
    } catch (resumeError) {
      toast({
        variant: 'destructive',
        title: 'Не удалось возобновить подписку',
        description:
          resumeError instanceof Error ? resumeError.message : 'Повторите попытку через минуту.',
      });
    } finally {
      setIsResuming(false);
    }
  }, [isResuming, refreshBillingPage, toast]);

  const handleCancelPendingPlan = useCallback(async () => {
    if (isCancellingPendingPlan) {
      return;
    }
    setIsCancellingPendingPlan(true);
    try {
      const updated = await cancelPendingPlanChange();
      setSubscription(updated);
      toast({
        title: 'Запланированное изменение отменено',
        description: 'Текущий тариф останется активным.',
      });
      await refreshBillingPage({ silent: true });
    } catch {
      // toast already shown
    } finally {
      setIsCancellingPendingPlan(false);
    }
  }, [cancelPendingPlanChange, isCancellingPendingPlan, refreshBillingPage, toast]);

  const handleViewInvoices = useCallback(() => {
    invoicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleDismissPlanPreview = useCallback(
    (open: boolean) => {
      if (open || isConfirmingPlanChange) {
        setIsPlanConfirmOpen(true);
        return;
      }
      setIsPlanConfirmOpen(false);
      setPlanPreview(null);
      setPlanPreviewSlug(null);
      setPlanPreviewBehavior(null);
      setHasPlanChangeRecurringConsent(false);
    },
    [isConfirmingPlanChange],
  );

  const handleConfirmPlanChange = useCallback(async () => {
    if (!planPreviewSlug) {
      setIsPlanConfirmOpen(false);
      setPlanPreview(null);
      setPlanPreviewBehavior(null);
      return;
    }
    const effectiveImmediately = planPreviewBehavior?.effectiveImmediately ?? true;
    setIsConfirmingPlanChange(true);
    try {
      if (effectiveImmediately) {
        if (!hasPlanChangeRecurringConsent) {
          return;
        }
        await startCheckout({
          plan: planPreviewSlug,
          mode: 'subscription',
          recurring_consent: { accepted: true, source: 'billing_confirm' },
        });
      } else {
        const updated = await changePlan(planPreviewSlug, { effectiveImmediately });
        setSubscription(updated);
        const scheduledDate =
          planPreviewBehavior?.deferredEffectiveAt ?? subscription?.current_period_end ?? null;
        toast({
          title: `Понижение запланировано на ${formatDateLabel(scheduledDate, 'следующее продление')}`,
          description:
            'Текущий тариф останется активным до указанной даты, затем переключение произойдет автоматически.',
        });
        await refreshBillingPage({ silent: true });
      }
      setIsPlanConfirmOpen(false);
      setPlanPreview(null);
      setPlanPreviewSlug(null);
      setPlanPreviewBehavior(null);
      setHasPlanChangeRecurringConsent(false);
    } catch {
      // changePlan already surfaced the error
    } finally {
      setIsConfirmingPlanChange(false);
    }
  }, [
    planPreviewSlug,
    changePlan,
    startCheckout,
    toast,
    refreshBillingPage,
    planPreviewBehavior,
    subscription?.current_period_end,
    hasPlanChangeRecurringConsent,
  ]);

  const handleCheckoutIntentPlan = useCallback(
    (plan: 'basic' | 'pro') => {
      void handlePlanCheckout(plan, 'subscription');
    },
    [handlePlanCheckout],
  );

  const notices: Notice[] = useMemo(() => {
    const items: Notice[] = [];
    if (isPastDue) {
      items.push({
        id: 'past-due',
        tone: 'danger',
        title: 'Оплата не прошла',
        description:
          'Последний платеж не удалось обработать. Откройте оплату заново, чтобы сразу восстановить платный тариф.',
        action: currentPaidPlan
          ? {
              label: 'Оплатить тариф',
              onClick: () => handleRecoverCurrentPlan(),
            }
          : undefined,
      });
    }
    if (cardExpiresSoon) {
      const label =
        cardExpiryMonth && cardExpiryYear
          ? `Карта истекает ${String(cardExpiryMonth).padStart(2, '0')}/${String(cardExpiryYear).slice(-2)}`
          : 'Срок действия карты скоро закончится';
      items.push({
        id: 'card-expiring',
        tone: 'warning',
        title: label,
        description: 'Следующее платное продление лучше провести заранее, чтобы привязать актуальную карту без перерыва в работе.',
        action: currentPaidPlan
          ? {
              label: 'Открыть оплату',
              onClick: () => handleRecoverCurrentPlan(),
            }
          : undefined,
      });
    }
    if (isCancelScheduled && subscription?.current_period_end) {
      items.push({
        id: 'scheduled-cancel',
        tone: 'warning',
        title: 'Отмена запланирована',
        description: `Платный тариф закончится ${formatDateLabel(subscription.current_period_end)}, если вы не возобновите подписку.`,
        action: {
          label: 'Возобновить тариф',
          onClick: () => handleResume(),
        },
      });
    }
    return items;
  }, [
    cardExpiresSoon,
    cardExpiryMonth,
    cardExpiryYear,
    currentPaidPlan,
    handleRecoverCurrentPlan,
    handleResume,
    isCancelScheduled,
    isPastDue,
    subscription?.current_period_end,
  ]);

  const planChipLabel = effectivePlanSlug ? `Тариф ${planLabel}` : planLabel;
  const overviewChips = [
    { label: planChipLabel, tone: 'info' as NoticeTone },
    { label: statusDetails.label, tone: statusDetails.tone },
  ];
  if (isCancelScheduled && subscription?.current_period_end) {
    overviewChips.push({
      label: `Отмена ${formatDateShort(subscription.current_period_end)}`,
      tone: 'warning' as NoticeTone,
    });
  }
  if (pendingPlanSlug) {
    const pendingDateLabel = pendingPlanEffectiveAt
      ? formatDateShort(pendingPlanEffectiveAt)
      : 'следующее продление';
    overviewChips.push({
      label: `Переход на ${pendingPlanName ?? formatPlanName(pendingPlanSlug)} ${pendingDateLabel}`,
      tone: 'warning' as NoticeTone,
    });
  }

  const changePlanLabel = isPendingVerification ? 'Подтвердить карту' : 'Сменить тариф';
  const isCurrentHobbyPlan = effectivePlanSlug === 'hobby' && !isPendingVerification;
  const needsHobbyVerification = effectivePlanSlug === 'hobby' && isPendingVerification;
  const showPublicAuthCta = !isAuthenticated;
  const showAccountPanels = !isAuthLoading && isAuthenticated;

  const renderPublicPlanPrimaryAction = (choice: (typeof displayedPlanChoices)[number]) => {
    if (!isAuthenticated) {
      if (isBillingPaidPlanSlug(choice.slug)) {
        return (
          <PublicLink href={withCheckoutPlanLink('/login', choice.slug)}>
            <Button className="w-full" data-testid={`billing-public-plan-${choice.slug}-login`}>
              Войти и подключить
            </Button>
          </PublicLink>
        );
      }

      return (
        <PublicLink href="/signup">
          <Button className="w-full" variant="outline" data-testid="billing-public-plan-hobby-signup">
            Создать аккаунт
          </Button>
        </PublicLink>
      );
    }

    if (!hasLoadedBilling) {
      return (
        <Button className="w-full" variant="outline" disabled>
          Загружаем аккаунт…
        </Button>
      );
    }

    if (choice.slug === 'hobby') {
      if (needsHobbyVerification) {
        return (
          <Button
            className="w-full"
            onClick={() => void handlePlanCheckout('hobby', 'setup')}
            disabled={pendingCheckoutPlan !== null || previewLoadingPlan !== null}
          >
            {pendingCheckoutPlan === 'hobby' ? 'Переадресация…' : 'Подтвердить карту'}
          </Button>
        );
      }

      if (isCurrentHobbyPlan) {
        return (
          <Button className="w-full" variant="outline" disabled>
            Текущий тариф
          </Button>
        );
      }

      return (
        <Button className="w-full" variant="outline" onClick={() => setIsPlanDialogOpen(true)}>
          Перейти на Хобби
        </Button>
      );
    }

    if (effectivePlanSlug === choice.slug && !isPendingVerification) {
      return (
        <Button className="w-full" variant="outline" disabled>
          Текущий тариф
        </Button>
      );
    }

    const isProcessing = pendingCheckoutPlan === choice.slug || previewLoadingPlan === choice.slug;
    const isOtherPlanBusy = Boolean(
      (pendingCheckoutPlan && pendingCheckoutPlan !== choice.slug) ||
      (previewLoadingPlan && previewLoadingPlan !== choice.slug),
    );

    return (
      <Button
        className="w-full"
        onClick={() => void handlePlanCheckout(choice.slug, choice.mode)}
        disabled={isOtherPlanBusy}
        data-testid={`billing-public-plan-${choice.slug}-checkout`}
      >
        {isProcessing ? 'Переход…' : `Перейти на ${choice.name}`}
      </Button>
    );
  };

  const renderPublicPlanSecondaryAction = (choice: (typeof displayedPlanChoices)[number]) => {
    if (isAuthenticated || !isBillingPaidPlanSlug(choice.slug)) {
      return null;
    }

    return (
      <PublicLink
        href={withCheckoutPlanLink('/signup', choice.slug)}
        className="inline-flex min-h-11 items-center rounded-xl px-3 py-2 text-sm font-medium text-[var(--site-accent-text)] underline-offset-2 hover:bg-[var(--site-accent-soft)] hover:underline"
        data-testid={`billing-public-plan-${choice.slug}-signup`}
      >
        Создать аккаунт под этот тариф
      </PublicLink>
    );
  };

  const renderQuickActions = () => {
    const isPlanActionBusy = Boolean(pendingCheckoutPlan) || previewLoadingPlan !== null;
    const primaryPlanButtonLabel = previewLoadingPlan
      ? 'Расчет…'
      : pendingCheckoutPlan
        ? 'Переадресация…'
        : changePlanLabel;
    const planButtonDisabled = isAccountBlocked || isPlanActionBusy || !hasLoadedBilling;
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="billing-quick-actions">
        <Button
          data-testid="billing-change-plan-button"
          className="w-full bg-[var(--site-accent-text)] hover:bg-[var(--site-primary-fill-strong)]"
          onClick={
            isPlanActionBusy
              ? undefined
              : isPendingVerification
                ? () => handlePlanCheckout('hobby', 'setup')
                : () => setIsPlanDialogOpen(true)
          }
          disabled={planButtonDisabled}
        >
          {primaryPlanButtonLabel}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={handleViewInvoices}
          data-testid="billing-view-invoices-button"
        >
          Счета
        </Button>
        {isCanceled ? (
          <Button
            variant="outline"
            className="w-full"
            disabled
            data-testid="billing-plan-secondary-action"
            data-action="canceled"
          >
            Подписка отменена
          </Button>
        ) : pendingPlanSlug ? (
          <Button
            variant="outline"
            className="w-full"
            onClick={handleCancelPendingPlan}
            disabled={isCancellingPendingPlan}
            data-testid="billing-plan-secondary-action"
            data-action="cancel-pending-change"
          >
            {isCancellingPendingPlan ? 'Отмена…' : 'Отменить запланированное изменение'}
          </Button>
        ) : isCancelScheduled ? (
          <Button
            variant="outline"
            className="w-full"
            onClick={handleResume}
            disabled={isResuming}
            data-testid="billing-plan-secondary-action"
            data-action="resume-plan"
          >
            {isResuming ? 'Возобновление…' : 'Возобновить тариф'}
          </Button>
        ) : (
          <Button
            variant="outline"
            className="w-full"
            onClick={currentPaidPlan ? () => setIsScheduleCancelConfirmOpen(true) : handleScheduleCancel}
            disabled={isCancelling}
            data-testid="billing-plan-secondary-action"
            data-action="schedule-cancel"
          >
            {isCancelling ? 'Планируем…' : 'Отменить в конце периода'}
          </Button>
        )}
      </div>
    );
  };

  const isInitialLoading = isLoading && !hasLoadedBilling;
  const loadingPlanSlug = normalizeBillingPlanSlug(user?.plan);
  const shouldShowLoadingOverageCard =
    loadingPlanSlug === null ? true : isBillingPaidPlanSlug(loadingPlanSlug);

  return (
    <AppPageShell
      hero={
        <PageHero>
          <PageIntro
            eyebrow="Биллинг"
            title="Подписка, счета и лимиты"
            description="Следите за подпиской, проверяйте лимиты и управляйте оплатой в одном месте."
          />
        </PageHero>
      }
    >
      <PageSection className="space-y-8">
        <AnimatedContainer animation={showPublicAuthCta ? 'none' : 'slideUp'} className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-widest text-[var(--site-accent-text)]">
            Биллинг и лимиты
          </p>
          <p className="max-w-3xl text-sm text-[var(--site-ink-muted)]">
            Публичные тарифы ниже рендерятся из deploy-time snapshot сразу в HTML. После входа здесь же появляются ваши счета, использование и управление подпиской.
          </p>
        </AnimatedContainer>

        {showPublicAuthCta ? (
          <AnimatedContainer animation={showPublicAuthCta ? 'none' : 'fadeIn'} delay={showPublicAuthCta ? 0 : 0.03}>
            <Card data-testid="billing-public-auth-cta">
              <CardHeader className="pb-4">
                <CardTitle>Войдите, чтобы управлять оплатой</CardTitle>
                <CardDescription>
                  После входа вы увидите текущую подписку, счета, историю использования и сможете сразу открыть checkout нужного тарифа.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                <PublicLink href="/login">
                  <Button>Войти</Button>
                </PublicLink>
                <PublicLink href="/signup">
                  <Button variant="outline">Создать аккаунт</Button>
                </PublicLink>
              </CardContent>
            </Card>
          </AnimatedContainer>
        ) : null}

        <AnimatedContainer animation={showPublicAuthCta ? 'none' : 'fadeIn'} delay={showPublicAuthCta ? 0 : 0.05}>
          <div className="grid gap-4 lg:grid-cols-3" data-testid="billing-public-plan-shell">
            {displayedPlanChoices.map((choice) => (
              <Card key={choice.slug} data-testid={`billing-public-plan-${choice.slug}`}>
                <CardHeader className="pb-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <CardTitle>{choice.name}</CardTitle>
                      <CardDescription>{choice.description}</CardDescription>
                    </div>
                    <p className="text-right text-lg font-semibold text-[var(--site-ink-strong)]">
                      {choice.price}
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-[var(--site-ink-muted)]">{choice.included}</p>
                  {choice.helper ? (
                    <p className="text-xs leading-relaxed text-[var(--site-ink-muted)]">{choice.helper}</p>
                  ) : null}
                  <div className="space-y-2">
                    {renderPublicPlanPrimaryAction(choice)}
                    {renderPublicPlanSecondaryAction(choice)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </AnimatedContainer>

        {showAccountPanels ? (
          <>
            {error ? (
              <Alert className="mt-6 border-red-200 bg-red-50 text-red-700">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Не удалось загрузить данные по оплате</AlertTitle>
                <AlertDescription>
                  {error}{' '}
                  <button
                    type="button"
                    className="font-semibold text-[var(--site-accent-text)] underline-offset-2 hover:underline"
                    onClick={() => void refreshBillingPage()}
                  >
                    Повторить
                  </button>
                </AlertDescription>
              </Alert>
            ) : null}

            {notices.length ? (
              <div className="mt-6 space-y-3">
                {notices.map((notice) => (
                  <Alert
                    key={notice.id}
                    className={`border ${
                      notice.tone === 'danger'
                        ? 'border-red-200 bg-red-50 text-red-700'
                        : notice.tone === 'warning'
                          ? 'border-amber-200 bg-amber-50 text-amber-800'
                          : 'border-[color:var(--site-accent-border)] bg-[color:rgba(8,145,178,0.1)] text-[var(--site-ink-strong)]'
                    }`}
                  >
                    {notice.tone === 'danger' ? (
                      <AlertCircle className="h-4 w-4" />
                    ) : notice.tone === 'warning' ? (
                      <AlertTriangle className="h-4 w-4" />
                    ) : (
                      <TrendingUp className="h-4 w-4" />
                    )}
                    <AlertTitle>{notice.title}</AlertTitle>
                    <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
                      <span>{notice.description}</span>
                      {notice.action ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={notice.action.onClick}
                          className="bg-white text-[var(--site-accent-text)]"
                        >
                          {notice.action.label}
                        </Button>
                      ) : null}
                    </AlertDescription>
                  </Alert>
                ))}
              </div>
            ) : null}

            {isInitialLoading ? (
              <div className="mt-8 space-y-8">
                <AnimatedContainer animation="fadeIn" delay={0.05}>
                  <BillingOverviewSkeleton />
                </AnimatedContainer>

                <div className="grid gap-8 lg:grid-cols-3">
                  <AnimatedContainer animation="fadeIn" delay={0.1} className="lg:col-span-2">
                    <BillingUsageSkeleton showOverageCard={shouldShowLoadingOverageCard} />
                  </AnimatedContainer>

                  <AnimatedContainer animation="fadeIn" delay={0.15}>
                    <BillingUpcomingChargeSkeleton />
                  </AnimatedContainer>
                </div>

                <AnimatedContainer animation="fadeIn" delay={0.2}>
                  <div ref={invoicesRef}>
                    <BillingInvoicesSkeleton />
                  </div>
                </AnimatedContainer>
              </div>
            ) : (
              <div className="mt-8 space-y-8">
                <AnimatedContainer animation="fadeIn" delay={0.05}>
                  <Card data-testid="billing-overview-card">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center justify-between gap-3">
                        Обзор подписки
                        <span className="text-sm font-normal text-[var(--site-ink-muted)]">
                          Период заканчивается {formatDateLabel(subscription?.current_period_end)}
                        </span>
                      </CardTitle>
                      <CardDescription>Статус подписки и основные действия</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="flex flex-wrap items-center gap-2" data-testid="billing-status-chips">
                        {overviewChips.map((chip) => (
                          <StatusChip key={chip.label} tone={chip.tone}>
                            {chip.label}
                          </StatusChip>
                        ))}
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div data-testid="current-plan-summary" data-plan-slug={effectivePlanSlug ?? ''}>
                          <p className="text-sm text-[var(--site-ink-muted)]">Текущий тариф</p>
                          <p
                            className="text-2xl font-semibold text-[var(--site-ink-strong)]"
                            data-testid="current-plan-name"
                          >
                            {planLabel}
                          </p>
                          <p className="text-sm text-[var(--site-ink-muted)]" data-testid="current-plan-allowance">
                            Включает {displayedIncludedUnits} запросов
                          </p>
                        </div>
                        <div data-testid="next-renewal-summary">
                          <p className="text-sm text-[var(--site-ink-muted)]">Следующее продление</p>
                          <p
                            className="text-2xl font-semibold text-[var(--site-ink-strong)]"
                            data-testid="next-renewal-amount"
                          >
                            {formatCurrency(upcomingInvoice?.amount_due, upcomingInvoice?.currency)}
                          </p>
                          <p className="text-sm text-[var(--site-ink-muted)]" data-testid="next-renewal-date">
                            {formatDateLabel(
                              upcomingInvoice?.next_payment_attempt ?? subscription?.current_period_end,
                            )}
                          </p>
                        </div>
                      </div>
                      {renderQuickActions()}
                    </CardContent>
                  </Card>
                </AnimatedContainer>

                <div className="grid gap-8 lg:grid-cols-3">
                  <AnimatedContainer animation="fadeIn" delay={0.1} className="lg:col-span-2">
                    <Card data-testid="billing-usage-card">
                      <CardHeader className="pb-4">
                        <CardTitle>Использование в текущем периоде</CardTitle>
                        <CardDescription>Прогресс месячной квоты и недавняя активность</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div>
                          <div className="flex items-center justify-between text-sm text-[var(--site-ink-muted)]">
                            <span>
                              {usageTotalsReady ? (
                                <>
                                  Использовано {displayedUsedUnits} / {displayedIncludedUnits} запросов
                                </>
                              ) : (
                                'Данные об использовании обновляются…'
                              )}
                            </span>
                            <span>{displayedUsagePercent}</span>
                          </div>
                          <div
                            className="relative mt-2 h-3 w-full rounded-full bg-[color:rgba(23,38,59,0.12)]"
                            data-testid="usage-progress-track"
                          >
                            <div
                              className={`absolute inset-y-0 left-0 rounded-full ${usageBarColor}`}
                              style={{ width: `${usageProgressWidth}%` }}
                              data-testid="usage-progress-bar"
                              data-used={usedUnits}
                              data-limit={includedUnits}
                            />
                            {usageThresholds.map((threshold) => (
                              <span
                                key={threshold.value}
                                className="absolute top-[-4px] h-4 w-0.5 bg-white/60"
                                style={{ left: `${threshold.value * 100}%` }}
                              />
                            ))}
                          </div>
                          <p className="mt-2 text-xs text-[var(--site-ink-muted)]">
                            Здесь показано измеренное использование для текущего расчетного периода, которое S-SHOT уже учел во внутреннем биллинге.
                          </p>
                        </div>
                        <div className="rounded-lg bg-[var(--site-surface-inset)] p-4 text-sm leading-relaxed text-[var(--site-ink)]">
                          <p>
                            {isHobbyPlan ? (
                              <>
                                Аккаунты на тарифе Хобби ставятся на паузу после {displayedIncludedUnits} запросов. Сброс произойдет {usageResetShort}.
                              </>
                            ) : (
                              <>
                                После {displayedIncludedUnits} запросов мы продолжаем обработку и учитываем перерасход
                                {overageRateDisplay ? ` по ставке ${overageRateDisplay}` : ''}. Любой перерасход попадет в следующий счет.
                              </>
                            )}
                          </p>
                        </div>
                        <div className={`grid gap-4 ${planSupportsOverage ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                          <div className="rounded-lg border border-[color:var(--site-border-soft)] p-4" data-testid="usage-used-card">
                            <p className="text-sm text-[var(--site-ink-muted)]">Использовано в периоде</p>
                            <p className="text-2xl font-semibold text-[var(--site-ink-strong)]" data-testid="usage-used-value">
                              {displayedUsedUnits}
                            </p>
                          </div>
                          <div className="rounded-lg border border-[color:var(--site-border-soft)] p-4" data-testid="usage-remaining-card">
                            <p className="text-sm text-[var(--site-ink-muted)]">Осталось запросов</p>
                            <p
                              className="text-2xl font-semibold text-[var(--site-ink-strong)]"
                              data-testid="usage-remaining-value"
                            >
                              {displayedRemainingUnits}
                            </p>
                          </div>
                          <div className="rounded-lg border border-[color:var(--site-border-soft)] p-4" data-testid="usage-reset-card">
                            <p className="text-sm text-[var(--site-ink-muted)]">Следующий сброс</p>
                            <p className="text-base font-semibold text-[var(--site-ink-strong)]" data-testid="usage-reset-value">
                              {usageResetFull}
                            </p>
                          </div>
                          {planSupportsOverage ? (
                            <div className="rounded-lg border border-[color:var(--site-border-soft)] p-4" data-testid="usage-overage-card">
                              <p className="text-sm text-[var(--site-ink-muted)]">Прогноз перерасхода</p>
                              <p
                                className="text-2xl font-semibold text-[var(--site-ink-strong)]"
                                data-testid="usage-overage-value"
                              >
                                {projectedOverageDisplay}
                              </p>
                              <p className="text-xs text-[var(--site-ink-muted)]">
                                {hasOverageCharges
                                  ? `Сверх лимита: ${overageUnits.toLocaleString()} ${overageUnits === 1 ? 'запрос' : 'запросов'}`
                                  : 'Перерасхода пока нет'}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>
                  </AnimatedContainer>

                  <AnimatedContainer animation="fadeIn" delay={0.15}>
                    <Card data-testid="billing-upcoming-charge-card">
                      <CardHeader className="pb-4">
                        <CardTitle>Следующее списание</CardTitle>
                        <CardDescription>Предпросмотр следующего счета</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {upcomingInvoice ? (
                          <>
                            <div>
                              <p className="text-sm text-[var(--site-ink-muted)]">Оценочная сумма</p>
                              <p
                                className="text-3xl font-semibold text-[var(--site-ink-strong)]"
                                data-testid="upcoming-charge-amount"
                              >
                                {formatCurrency(upcomingInvoice.amount_due, upcomingInvoice.currency)}
                              </p>
                              <p className="text-sm text-[var(--site-ink-muted)]" data-testid="upcoming-charge-date">
                                {formatDateLabel(upcomingInvoice.next_payment_attempt)}
                              </p>
                            </div>
                            <div className="space-y-2 rounded-lg border border-[color:var(--site-border-soft)] p-4">
                              {upcomingInvoice.lines?.length ? (
                                upcomingInvoice.lines.map((line, index) => (
                                  <div
                                    key={`${line.description}-${index}`}
                                    className="flex items-center justify-between text-sm text-[var(--site-ink)]"
                                  >
                                    <span>{line.description}</span>
                                    <span className="font-medium">
                                      {formatCurrency(line.amount, upcomingInvoice.currency)}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <p className="text-sm text-[var(--site-ink-muted)]">Детали появятся ближе к моменту списания.</p>
                              )}
                            </div>
                            <p className="text-xs text-[var(--site-ink-muted)]">
                              Измеренное использование фиксируется в конце текущего периода и может измениться.
                            </p>
                          </>
                        ) : (
                          <div className="text-sm text-[var(--site-ink-muted)]">
                            Следующего счета пока нет. Списания появятся после продления подписки.
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </AnimatedContainer>
                </div>

                <AnimatedContainer animation="fadeIn" delay={0.2}>
                  <div ref={invoicesRef}>
                    <Card>
                      <CardHeader className="pb-4">
                        <CardTitle>Счета</CardTitle>
                        <CardDescription>История оплат и выставленных счетов по вашему аккаунту.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex flex-wrap gap-3">
                          <Select value={invoiceYearFilter} onValueChange={setInvoiceYearFilter}>
                            <SelectTrigger className="w-[160px]">
                              <SelectValue placeholder="Все годы" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Все годы</SelectItem>
                              {invoiceYears.map((year) => (
                                <SelectItem key={year} value={year}>
                                  {year}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={invoiceMonthFilter}
                            onValueChange={(value) =>
                              setInvoiceMonthFilter(value as (typeof MONTH_OPTIONS)[number]['value'])
                            }
                          >
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder="Все месяцы" />
                            </SelectTrigger>
                            <SelectContent>
                              {MONTH_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="rounded-lg border border-[color:var(--site-border-soft)]">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Дата</TableHead>
                                <TableHead>Счет #</TableHead>
                                <TableHead>Статус</TableHead>
                                <TableHead className="text-right">Сумма</TableHead>
                                <TableHead>Файл</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredInvoices.length ? (
                                filteredInvoices.map((invoice, index) => {
                                  const invoiceNumber =
                                    (invoice.invoice_number && invoice.invoice_number.trim().length
                                      ? invoice.invoice_number
                                      : invoice.invoice_id?.replace(/^in_/, '').toUpperCase()) ?? '—';
                                  const pdfUrl =
                                    invoice.invoice_pdf ||
                                    (invoice.metadata?.invoice_pdf as string | undefined) ||
                                    null;
                                  const rowKey =
                                    invoice.invoice_id ??
                                    (invoice.invoice_number && invoice.invoice_number.trim().length
                                      ? invoice.invoice_number
                                      : null) ??
                                    invoice.hosted_invoice_url ??
                                    invoice.invoice_pdf ??
                                    `${invoice.user_id}-${invoice.period_end ?? invoice.period_start ?? invoice.created ?? index}`;
                                  return (
                                    <TableRow key={rowKey}>
                                      <TableCell className="whitespace-nowrap">
                                        {formatDateLabel(invoice.period_end ?? invoice.created)}
                                      </TableCell>
                                      <TableCell>{invoiceNumber}</TableCell>
                                      <TableCell>
                                        <StatusChip tone={invoiceStatusTone(invoice.status)}>
                                          {formatStatusLabel(invoice.status)}
                                        </StatusChip>
                                      </TableCell>
                                      <TableCell className="text-right font-medium">
                                        {formatCurrency(invoice.total, invoice.currency)}
                                      </TableCell>
                                      <TableCell>
                                        {pdfUrl ? (
                                          <a
                                            href={pdfUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            download
                                            className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--site-accent-text)] hover:underline"
                                          >
                                            Скачать
                                            <Download className="h-3.5 w-3.5" />
                                          </a>
                                        ) : (
                                          <span className="text-sm text-[var(--site-ink-subtle)]">Недоступно</span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={5} className="py-8 text-center text-sm text-[var(--site-ink-muted)]">
                                    Для этого фильтра счета пока отсутствуют.
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </AnimatedContainer>
              </div>
            )}
          </>
        ) : null}
      </PageSection>

      <BillingCheckoutIntentHandler
        enabled={showAccountPanels && hasLoadedBilling}
        onCheckoutIntent={handleCheckoutIntentPlan}
      />

      <AlertDialog open={isScheduleCancelConfirmOpen} onOpenChange={handleScheduleCancelDialogOpenChange}>
        <AlertDialogContent data-testid="billing-schedule-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{scheduleCancelConfirmCopy.title}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <ScheduleCancelConfirmDialogBody
                  planName={scheduleCancelPlanName}
                  periodEndLabel={scheduleCancelPeriodEndLabel}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isCancelling}
              data-testid="billing-schedule-cancel-dismiss-button"
            >
              {scheduleCancelConfirmCopy.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isCancelling}
              data-testid="billing-schedule-cancel-confirm-button"
              className="bg-destructive text-destructive-foreground shadow-[var(--site-shadow-soft)] hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                void handleScheduleCancel();
              }}
            >
              {isCancelling
                ? scheduleCancelConfirmCopy.pendingConfirmLabel
                : scheduleCancelConfirmCopy.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isPlanDialogOpen} onOpenChange={setIsPlanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Сменить тариф</DialogTitle>
            <DialogDescription>
              Повышение запускает новый расчетный месяц и списывает оплату сегодня. Понижение вступает в силу на следующем продлении.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {displayedPlanChoices.map((choice) => {
              const isCurrent = effectivePlanSlug === choice.slug && !isPendingVerification;
              const isScheduledPlan = pendingPlanSlug === choice.slug;
              const isProcessing = pendingCheckoutPlan === choice.slug;
              const isPreviewing = previewLoadingPlan === choice.slug;
              const otherPlanBusy = Boolean(
                (pendingCheckoutPlan && pendingCheckoutPlan !== choice.slug) ||
                (previewLoadingPlan && !isPreviewing),
              );
              const isHobbyChoice = choice.slug === 'hobby';
              const inlineEligible = canInlineChangePlan(choice.slug);
              const inlineProcessing = inlineEligible && isProcessing;
              let buttonLabel = isPreviewing
                ? 'Расчет…'
                : isProcessing
                  ? inlineProcessing
                    ? 'Обновление…'
                    : 'Переадресация…'
                  : `Перейти на ${choice.name}`;
              let buttonDisabled = isCurrent || otherPlanBusy;
              let helperText = choice.helper;
              let onClick: (() => void) | undefined = () => handlePlanCheckout(choice.slug, choice.mode);

              if (isBillingPaidPlanSlug(choice.slug) && planPreviewSlug === choice.slug && isPlanConfirmOpen) {
                buttonLabel = isConfirmingPlanChange ? 'Обновление…' : 'Подтвердите в диалоге';
                buttonDisabled = true;
              }

              if (!hasLoadedBilling && !isHobbyChoice) {
                buttonLabel = 'Загружаем данные подписки…';
                buttonDisabled = true;
              }

              if (isHobbyChoice) {
                if (needsHobbyVerification) {
                  buttonLabel = isProcessing ? 'Переадресация…' : 'Подтвердить карту';
                  buttonDisabled = otherPlanBusy;
                  helperText =
                    'Одноразовая проверка карты открывает бесплатный тариф Хобби. Списаний не будет.';
                  onClick = () => handlePlanCheckout('hobby', 'setup');
                } else if (isCurrentHobbyPlan) {
                  buttonLabel = 'Текущий тариф';
                  buttonDisabled = true;
                  helperText = helperText ?? 'Карта уже подтверждена, дополнительных действий не требуется.';
                  onClick = undefined;
                } else if (isCancelScheduled) {
                  buttonLabel = 'Понижение запланировано';
                  buttonDisabled = true;
                  helperText =
                    helperText ?? 'Переход на Хобби произойдет в конце текущего расчетного периода.';
                  onClick = undefined;
                } else {
                  buttonLabel = isCancelling ? 'Планируем…' : 'Перейти на Хобби';
                  buttonDisabled = isCancelling;
                  helperText =
                    helperText ?? 'Мы запланируем понижение в конце текущего цикла. Дополнительная проверка карты не требуется.';
                  onClick = () => {
                    void handleDowngradeToHobby();
                  };
                }
              }
              if (isScheduledPlan) {
                const effectiveCopy = formatDateLabel(pendingPlanEffectiveAt, 'следующее продление');
                helperText =
                  helperText ?? `Понижение запланировано. Этот тариф станет активным ${effectiveCopy}.`;
                buttonLabel = 'Понижение запланировано';
                buttonDisabled = true;
              }

              return (
                <div
                  key={choice.slug}
                  className="rounded-xl border border-[color:var(--site-border-soft)] p-4 shadow-sm"
                  data-testid={`plan-option-${choice.slug}`}
                  data-plan-status={
                    isCurrent ? 'current' : isScheduledPlan ? 'scheduled' : isPreviewing ? 'previewing' : 'available'
                  }
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-lg font-semibold text-[var(--site-ink-strong)]">{choice.name}</p>
                      {isCurrent ? <StatusChip tone="info">Текущий тариф</StatusChip> : null}
                      {isScheduledPlan ? <StatusChip tone="warning">Запланировано</StatusChip> : null}
                    </div>
                    <p className="text-sm text-[var(--site-ink)]">{choice.description}</p>
                    <p className="text-sm font-medium text-[var(--site-ink-muted)]">
                      {choice.price} · {choice.included}
                    </p>
                    {helperText ? (
                      <p className="text-xs text-[var(--site-ink-muted)]">{helperText}</p>
                    ) : null}
                    <Button
                      className="mt-2 w-full sm:w-auto"
                      onClick={onClick}
                      disabled={buttonDisabled}
                      data-testid={`plan-option-${choice.slug}-cta`}
                    >
                      {buttonLabel}
                    </Button>
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-[var(--site-ink-muted)]">
              Нужен больший объем?{' '}
              <PublicLink href="/contact" className="text-[var(--site-accent-text)] underline-offset-2 hover:underline">
                Свяжитесь с продажами
              </PublicLink>{' '}
              для индивидуального тарифа.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isPlanConfirmOpen} onOpenChange={handleDismissPlanPreview}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Подтвердите смену тарифа</DialogTitle>
            <DialogDescription>
              {planPreviewBehavior?.effectiveImmediately === false
                ? 'Понижение откладывается до следующего продления. Проверьте запланированное изменение и подтвердите его, когда будете готовы.'
                : 'Проверьте сумму немедленного списания перед сменой тарифа. После подтверждения мы перенаправим вас в Robokassa.'}
            </DialogDescription>
          </DialogHeader>
          {planPreview ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-[color:var(--site-border-soft)] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--site-ink)]">Тариф</span>
                  <span className="text-sm font-semibold text-[var(--site-ink-strong)] text-right whitespace-nowrap">
                    {(planPreviewSlug && PLAN_NAME_LOOKUP[planPreviewSlug]) ?? planPreview.plan_name}
                  </span>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--site-ink-muted)]">Списать сегодня</p>
                    <p className="text-sm text-[var(--site-ink)]">
                      {planPreview.amount_due_now > 0
                        ? 'Мы откроем оплату полной стоимости нового периода сейчас и перенесем дату продления после успешного платежа.'
                        : planPreview.amount_due_now === 0
                          ? planPreviewDeferredDate
                            ? `Сегодня платеж не требуется. Изменение вступит в силу ${formatDateLabel(planPreviewDeferredDate, 'следующее продление')} и будет продлеваться уже по новой ставке.`
                            : 'Сегодня платеж не требуется. Понижение вступит в силу на следующем продлении после планирования.'
                          : 'Если сумма сегодня отрицательная, корректировка зачтется во внутреннем расчете следующего продления.'}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-[var(--site-ink-strong)] text-right whitespace-nowrap">
                    {formatCurrency(planPreview.amount_due_now, planPreview.currency)}
                  </span>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--site-ink-muted)]">
                      Будущие продления
                    </p>
                    <p className="text-sm text-[var(--site-ink)]">
                      {planPreview.next_interval_amount != null
                        ? `Будет списываться каждые ${formatIntervalFrequency(planPreview.next_interval, planPreview.next_interval_count)}, начиная со следующего периода.`
                        : 'Будущие продления будут рассчитываться по ставке выбранного тарифа.'}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-[var(--site-ink-strong)] text-right whitespace-nowrap">
                    {planPreview.next_interval_amount != null
                      ? formatCurrency(planPreview.next_interval_amount, planPreview.currency)
                      : '—'}
                  </span>
                </div>
                {planPreviewDeferredDate ? (
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--site-ink-muted)]">
                        Дата вступления в силу
                      </p>
                      <p className="text-sm text-[var(--site-ink)]">
                        Мы переключим тариф в эту дату, а текущий тариф останется активным до этого момента.
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-[var(--site-ink-strong)] text-right whitespace-nowrap">
                      {formatDateLabel(planPreviewDeferredDate, 'следующее продление')}
                    </span>
                  </div>
                ) : null}
              </div>

              <div className="rounded-lg border border-dashed border-[color:var(--site-border-soft)] p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--site-ink-muted)]">
                  Детали перерасчета
                </p>
                {planPreviewLines.length ? (
                  <ul className="space-y-2">
                    {planPreviewLines.map((line, index) => {
                      const label = line.description ?? toTitle(line.type);
                      const isProration = Boolean(line.proration);
                      return (
                        <li
                          key={`${label || 'line'}-${index}`}
                          className="flex flex-wrap items-center justify-between gap-2 text-sm text-[var(--site-ink)]"
                        >
                          <div className="flex items-center gap-2">
                            <span>{label || 'Корректировка'}</span>
                            {isProration ? <StatusChip tone="info">Перерасчет</StatusChip> : null}
                          </div>
                          <span className="font-medium text-[var(--site-ink-strong)]">
                            {formatCurrency(line.amount ?? 0, planPreview.currency)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-[var(--site-ink-muted)]">
                    Для этого изменения перерасчет не требуется.
                  </p>
                )}
                <p className="text-xs text-[var(--site-ink-muted)]">
                  {!planPreviewLines.length || hasProrationAdjustments
                    ? planPreviewBehavior?.effectiveImmediately === false
                      ? 'Понижения не создают перерасчет. Текущий более высокий тариф останется активным до указанной даты, а затем переключение произойдет автоматически.'
                      : 'Эти корректировки закрывают предыдущий расчетный период, поэтому все более раннее использование остается тарифицированным по прежнему тарифу.'
                    : 'Для этого изменения перерасчет не требуется, поэтому строки выше показывают итог ближайшего продления.'}
                </p>
              </div>

              {planPreviewBehavior?.effectiveImmediately !== false ? (
                <RecurringConsentBlock
                  checked={hasPlanChangeRecurringConsent}
                  onCheckedChange={setHasPlanChangeRecurringConsent}
                  amountLabel={formatCurrency(
                    planPreview.next_interval_amount ?? planPreview.amount_due_now,
                    planPreview.currency,
                  )}
                  frequencyLabel={formatRecurringCadence(
                    planPreview.next_interval,
                    planPreview.next_interval_count,
                  )}
                  checkboxId="billing-recurring-consent"
                />
              ) : null}

              <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => handleDismissPlanPreview(false)}
                  disabled={isConfirmingPlanChange}
                >
                  Не сейчас
                </Button>
                <Button
                  onClick={handleConfirmPlanChange}
                  disabled={
                    isConfirmingPlanChange ||
                    (planPreviewBehavior?.effectiveImmediately !== false && !hasPlanChangeRecurringConsent)
                  }
                >
                  {isConfirmingPlanChange ? 'Обновление…' : 'Подтвердить смену тарифа'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[var(--site-ink)]">Загрузка деталей предпросмотра…</p>
          )}
        </DialogContent>
      </Dialog>
    </AppPageShell>
  );
}
